//
//  TokenUtilPreviewMetadataGenerator.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { BlurType } from "../../model/enums/SDKBlurType";
import { GradientType } from "../../model/enums/SDKGradientType";
import { ShadowType } from "../../model/enums/SDKShadowType";
import { TextCase } from "../../model/enums/SDKTextCase";
import { TextDecoration } from "../../model/enums/SDKTextDecoration";
import { TokenType } from "../../model/enums/SDKTokenType";
import { VisibilityType } from "../../model/enums/SDKVisibilityType";
import { DimensionToken, } from "../../model/tokens/SDKDimensionToken";
import { TokenUtils } from "../../utils/TokenUtils";
import { TokenTransform } from "../token-transform/SDKToolsTokenTransform";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Utility
export class TokenUtilPreviewMetadataGenerator {
    resolvedMetadataForToken(token, allTokens, layerIndex) {
        var _a, _b, _c, _d, _e, _f;
        const { value } = token;
        // Multi-layer tokens (gradient, shadow) hold an array of layers. Pick a
        // single layer for the preview meta — default to layer 0; callers
        // rendering per-layer swatches (e.g. shadow detail rows) pass the row's index.
        let plainValue;
        if (Array.isArray(value)) {
            const index = layerIndex !== null && layerIndex !== void 0 ? layerIndex : 0;
            const layer = (_a = value[index]) !== null && _a !== void 0 ? _a : value[0];
            if (!layer) {
                throw new Error("Multi-layer token has no layers to preview");
            }
            plainValue = layer;
        }
        else {
            plainValue = value;
        }
        let meta = null;
        switch (token.tokenType) {
            case TokenType.shadow: {
                const v = plainValue;
                meta = {
                    x: v.x,
                    y: v.y,
                    type: v.type === ShadowType.drop ? "drop" : "inner",
                };
                break;
            }
            case TokenType.typography: {
                const v = plainValue;
                meta = {
                    fontSize: {
                        value: v.fontSize.measure,
                        unit: DimensionToken.unitToString(v.fontSize.unit),
                    },
                };
                break;
            }
            case TokenType.fontSize: {
                const v = plainValue;
                meta = {
                    fontSize: {
                        value: v.measure,
                        unit: DimensionToken.unitToString(v.unit),
                    },
                };
                break;
            }
            case TokenType.blur: {
                const v = plainValue;
                meta = {
                    type: v.type === BlurType.background ? "background" : "layer",
                };
                break;
            }
            case TokenType.dimension:
            case TokenType.size:
            case TokenType.space:
            case TokenType.opacity:
            case TokenType.lineHeight:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
            case TokenType.borderWidth:
            case TokenType.radius:
            case TokenType.duration:
            case TokenType.zIndex: {
                const v = plainValue;
                meta = {
                    value: v.measure,
                    unit: DimensionToken.unitToString(v.unit),
                };
                break;
            }
            default:
                break;
        }
        return {
            displayValue: this.displayValue(token),
            tokenType: this.tokenTypeToPreviewType(token.tokenType),
            referencedTo: (_c = (_b = TokenUtils.lookupReference(plainValue.referencedTokenId, allTokens)) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : undefined,
            referenceResolutionFailed: TokenUtils.isReferenceResolutionFailed(token),
            referenceOriginRemote: !!((_d = token.origin) === null || _d === void 0 ? void 0 : _d.referenceOriginRemote),
            referenceOriginName: (_f = (_e = token.origin) === null || _e === void 0 ? void 0 : _e.referenceOriginName) !== null && _f !== void 0 ? _f : undefined,
            // @ts-expect-error TS(2322): Type '{ x: number; y: number; type: string; }' is ... Remove this comment to see the full error message
            meta,
        };
    }
    displayValue(token) {
        switch (token.tokenType) {
            case TokenType.color: {
                const t = token;
                return t.toHex8().toUpperCase();
            }
            case TokenType.blur: {
                const t = token;
                return DimensionToken.valueToString(t.value.radius, {});
            }
            case TokenType.shadow: {
                const t = token;
                const transform = new TokenTransform();
                return transform.shadowsTokenToCSS(t.value);
            }
            case TokenType.gradient: {
                const t = token;
                const layer = t.value[0];
                if (layer) {
                    switch (layer.type) {
                        case GradientType.linear:
                            return "linear";
                        case GradientType.angular:
                            return "angular";
                        case GradientType.radial:
                            return "radial";
                        default:
                            throw new Error(`Unknown gradient type`);
                    }
                }
                return "linear";
            }
            case TokenType.fontFamily: {
                const t = token;
                return t.value.text;
            }
            case TokenType.fontWeight: {
                const t = token;
                return t.value.text;
            }
            case TokenType.fontSize: {
                const t = token;
                return DimensionToken.valueToString(t.value);
            }
            case TokenType.lineHeight: {
                const t = token;
                return DimensionToken.valueToString(t.value);
            }
            case TokenType.letterSpacing: {
                const t = token;
                return DimensionToken.valueToString(t.value);
            }
            case TokenType.paragraphSpacing: {
                const t = token;
                return DimensionToken.valueToString(t.value);
            }
            case TokenType.opacity: {
                const t = token;
                return `${t.value.measure}`;
            }
            case TokenType.border: {
                const t = token;
                const transform = new TokenTransform();
                return transform.borderTokenToCSS(t.value);
            }
            case TokenType.radius: {
                const t = token;
                return DimensionToken.valueToString(t.value);
            }
            case TokenType.borderWidth: {
                const t = token;
                return DimensionToken.valueToString(t.value);
            }
            case TokenType.duration: {
                const t = token;
                return DimensionToken.valueToString(t.value);
            }
            case TokenType.zIndex: {
                const t = token;
                return `${t.value.measure}`;
            }
            case TokenType.textCase: {
                const t = token;
                // Value is stored loosely as string but always holds a TextCase
                switch (t.value.value) {
                    case TextCase.camel:
                        return "capitalize";
                    case TextCase.lower:
                        return "lowercase";
                    case TextCase.original:
                        return "none";
                    case TextCase.smallCaps:
                        return "SmallCaps";
                    case TextCase.upper:
                        return "uppercase";
                    default:
                        throw new Error(`Unknown text case`);
                }
            }
            case TokenType.textDecoration: {
                const t = token;
                // Value is stored loosely as string but always holds a TextDecoration
                switch (t.value.value) {
                    case TextDecoration.original:
                        return "none";
                    case TextDecoration.underline:
                        return "underline";
                    case TextDecoration.strikethrough:
                        return "strikethrough";
                    default:
                        throw new Error(`Unknown text decoration`);
                }
            }
            case TokenType.visibility: {
                const t = token;
                // Value is stored loosely as string but always holds a VisibilityType
                return t.value.value === VisibilityType.visible
                    ? "true"
                    : "false";
            }
            case TokenType.productCopy: {
                const t = token;
                return t.value.text;
            }
            case TokenType.string: {
                const t = token;
                return t.value.text;
            }
            case TokenType.dimension: {
                const t = token;
                return DimensionToken.valueToString(t.value);
            }
            case TokenType.size: {
                const t = token;
                return DimensionToken.valueToString(t.value);
            }
            case TokenType.space: {
                const t = token;
                return DimensionToken.valueToString(t.value);
            }
            case TokenType.typography: {
                const t = token;
                const transform = new TokenTransform();
                return transform.typographyTokenToCSS(t.value);
            }
            default: {
                throw new Error(`Unknown token type`);
            }
        }
    }
    tokenTypeToPreviewType(type) {
        switch (type) {
            case TokenType.color:
                return "color";
            case TokenType.typography:
                return "typography";
            case TokenType.radius:
                return "radius";
            case TokenType.dimension:
                return "dimension";
            case TokenType.size:
                return "size";
            case TokenType.space:
                return "space";
            case TokenType.opacity:
                return "opacity";
            case TokenType.fontSize:
                return "fontSize";
            case TokenType.lineHeight:
                return "lineHeight";
            case TokenType.letterSpacing:
                return "letterSpacing";
            case TokenType.paragraphSpacing:
                return "paragraphSpacing";
            case TokenType.borderWidth:
                return "borderWidth";
            case TokenType.duration:
                return "duration";
            case TokenType.zIndex:
                return "zIndex";
            case TokenType.shadow:
                return "shadow";
            case TokenType.border:
                return "border";
            case TokenType.gradient:
                return "gradient";
            case TokenType.string:
                return "string";
            case TokenType.productCopy:
                return "copy";
            case TokenType.fontFamily:
                return "fontFamily";
            case TokenType.fontWeight:
                return "fontWeight";
            case TokenType.textCase:
                return "textCase";
            case TokenType.textDecoration:
                return "textDecoration";
            case TokenType.visibility:
                return "visibility";
            case TokenType.blur:
                return "blur";
            default:
                throw new Error("Unknown token type");
        }
    }
}
